export const frenchHumanoidRobotsPage = {
  path: "/humanoid-robots/france",
  title: "French Humanoid Robots France’s Humanoid Robotics Ecosystem",
  seoTitle: "French Humanoid Robots Wandercraft, Reachy 2, Mirokaï, NAO and More",
  seoDescription:
    "Explore France’s humanoid robotics ecosystem, from Wandercraft’s Calvin and Pollen Robotics’ Reachy 2 to Enchanted Tools’ Mirokaï, Aldebaran’s NAO and French research platforms.",
  description:
    "French humanoid robotics includes industrial humanoids, open-source research platforms, social robots, assistive robotics and embodied AI labs.",
  updatedAt: "October 11, 2026",
  author: "TechniaHQRobot Robotics Desk",
  readingTime: "9 min read",
  keywords: [
    "French humanoid robots",
    "humanoid robots in France",
    "France robotics ecosystem",
    "Wandercraft Calvin",
    "Pollen Robotics Reachy 2",
    "Enchanted Tools Mirokaï",
    "Aldebaran NAO",
    "Pepper robot",
    "Poppy Humanoid",
    "humanoid robotics Europe",
    "embodied AI France"
  ],
  summaryCards: [
    {
      label: "Industrial humanoid programmes",
      value: "1",
      detail: "Wandercraft reports Calvin-40 at Renault Douai. First series deliveries are expected in H1 2027."
    },
    {
      label: "Open-source and research robots",
      value: "3+",
      detail: "Reachy 2, Poppy Humanoid and HRP-2 research work show the lab side of the French ecosystem."
    },
    {
      label: "Social and service robots",
      value: "3",
      detail: "Mirokaï, NAO and Pepper connect French humanoid robotics to education, healthcare and public-facing service."
    },
    {
      label: "Historical French platforms",
      value: "3",
      detail: "NAO, Pepper and Romeo shaped the first wave of French humanoid robotics."
    }
  ],
  robots: [
    {
      id: "calvin-40",
      name: "Calvin / Calvin-40",
      company: "Wandercraft",
      city: "Paris",
      status: "Development with reported factory use",
      statusTone: "active",
      mainUse: "Industrial handling of loads, boxes and crates. Wandercraft describes picking and kitting as capabilities under expansion.",
      mobility: "Biped humanoid industrial mobility with autonomous task navigation described by Wandercraft.",
      keyHardware: "High-torque motors, high-strength structure, 3D sensing, neural-network vision, whole-body load-adaptive control and safety system described as certifiable by Wandercraft.",
      autonomy: "Wandercraft describes autonomous navigation and task execution. Its 2026 timeline reports operation at Renault Douai; no independent task-success or intervention record was inspected.",
      whyItMatters:
        "Renault and Wandercraft announced the programme on 6 June 2025. The manufacturer now reports factory use, while its product page expects first series delivery in H1 2027. An early operating site and a series delivery schedule describe different stages.",
      sourceLabel: "Wandercraft Calvin-40",
      sourceUrl: "https://en.wandercraft.eu/calvin",
      additionalSources: [{"label": "Renault partnership, June 2025", "url": "https://www.wandercraft.eu/articles/renault-group-finalises-a-strategic-partnership-with-wandercraft"}, {"label": "Wandercraft 2026 factory-use report", "url": "https://en.wandercraft.eu/about-us"}]
    },
    {
      id: "reachy-2",
      name: "Reachy 2",
      company: "Pollen Robotics / Hugging Face",
      city: "Bordeaux",
      status: "Active open-source research",
      statusTone: "active",
      mainUse: "Embodied AI research, bimanual manipulation, teleoperation and robotics lab prototyping.",
      mobility: "Upper-body humanoid manipulator available with a mobile base using three omniwheels and LiDAR.",
      keyHardware: "Bimanual arms, 7-DoF arm design, camera-based head, mobile base option, Python SDK, VR teleoperation and ROS 2 Humble support.",
      autonomy: "Designed for teleoperation, Python control and embodied AI research. Do not treat it as a general-purpose autonomous home robot.",
      whyItMatters:
        "Reachy 2 gives AI labs a transparent humanoid platform. Its open-source positioning matters because robot learning needs hardware, code and datasets that researchers can inspect and modify.",
      sourceLabel: "Pollen Robotics Reachy 2",
      sourceUrl: "https://www.pollen-robotics.com/reachy/"
    },
    {
      id: "mirokai",
      name: "Miroki / Miroka / Mirokaï",
      company: "Enchanted Tools",
      city: "Paris",
      status: "Active service robot",
      statusTone: "active",
      mainUse: "People-facing service in hospitals, senior living, hotels, restaurants, retail, events, museums and airports.",
      mobility: "Indoor mobile humanoid service robot with adaptive navigation for shared public spaces.",
      keyHardware: "Expressive character design, arms for carrying and grasping, protected joints, lower center of gravity, visual feedback, cellular connectivity, Wi-Fi 6E and autonomous charging according to Enchanted Tools.",
      autonomy: "Enchanted Tools describes autonomous navigation, charging and decision-making for operation with minimal supervision. Scope depends on deployment environment.",
      whyItMatters:
        "Mirokaï is a service platform aimed at interaction, care support and customer-facing spaces. These intended uses do not establish clinical outcomes or unattended reliability.",
      sourceLabel: "Enchanted Tools Mirokaï",
      sourceUrl: "https://enchanted.tools/robot/"
    },
    {
      id: "nao",
      name: "NAO",
      company: "Aldebaran / Maxtronics",
      city: "France / Issy-les-Moulineaux support",
      status: "Legacy / supported education platform",
      statusTone: "legacy",
      mainUse: "Education, programming, research, healthcare activities and human-robot interaction experiments.",
      mobility: "Small biped humanoid platform for indoor interaction and programmed motions.",
      keyHardware: "58 cm body, 25 degrees of freedom, cameras, microphones, tactile sensors, sonars and IMU listed by Maxtronics for NAO6.",
      autonomy: "Programmable platform with social and full-body motion capabilities. Autonomy depends on the application built by educators, researchers or developers.",
      whyItMatters:
        "NAO made French humanoid robotics visible in classrooms, labs and RoboCup. It remains a reference platform for teaching robot programming and human-robot interaction.",
      sourceLabel: "Maxtronics NAO6",
      sourceUrl: "https://maxtronics.com/en/nao6/"
    },
    {
      id: "pepper",
      name: "Pepper",
      company: "Aldebaran / SoftBank Robotics",
      city: "French-origin platform, global deployment",
      status: "Discontinued / legacy social robot",
      statusTone: "historical",
      mainUse: "Customer reception, public interaction, social robotics research and education.",
      mobility: "Wheeled semi-humanoid service robot for indoor public-facing environments.",
      keyHardware: "Humanoid upper body, expressive head and arms, chest tablet, cameras and microphones in the classic Pepper platform.",
      autonomy: "Pepper could run scripted and app-based interactions. Production was halted in 2021, so this page labels it as legacy rather than active product growth.",
      whyItMatters:
        "Pepper proved how visible social robots could become in stores, museums, schools and research labs. Its commercial limits also showed that public interaction is not enough without strong utility and support economics.",
      sourceLabel: "Reuters / SoftBank Robotics context",
      sourceUrl: "https://www.reuters.com/business/media-telecom/softbank-ties-up-with-iris-ohyama-recharge-robotics-ambitions-2021-01-27/"
    },
    {
      id: "romeo",
      name: "Romeo",
      company: "Aldebaran with French research partners",
      city: "Paris and French research network",
      status: "Historical research platform",
      statusTone: "historical",
      mainUse: "Assistive humanoid research for elderly people or people with reduced autonomy.",
      mobility: "Large humanoid research platform designed around walking, perception, manipulation and assistive interaction research.",
      keyHardware: "Research platform reported around a 1.40 m humanoid body, with work on perception, walking, object carrying and assistive tasks.",
      autonomy: "Research platform. Do not present Romeo as a commercial or currently deployed assistive robot.",
      whyItMatters:
        "Romeo connects the Aldebaran era to French public research. It focused on assistance, acceptability, walking and manipulation before the current wave of embodied AI.",
      sourceLabel: "Romeo research project summary",
      sourceUrl: "https://fr.wikipedia.org/wiki/Romeo_(robot)"
    },
    {
      id: "poppy-humanoid",
      name: "Poppy Humanoid",
      company: "Inria / Poppy Project",
      city: "Bordeaux / Talence ecosystem",
      status: "Active open-source education and research platform",
      statusTone: "research",
      mainUse: "Education, makers, open hardware, robotics teaching and research experiments.",
      mobility: "3D-printed humanoid kit and related Poppy platform variants for educational robotics.",
      keyHardware: "Open-source robotic platform with assembly guides for Poppy Humanoid, Poppy Torso and Ergo Jr, with programming options documented for the Poppy ecosystem.",
      autonomy: "Programmable education platform. Autonomy depends on the project code, sensors and experiment design.",
      whyItMatters:
        "Poppy made humanoid robotics easier to inspect, assemble, modify and teach. It helped move French humanoid work beyond closed commercial robots.",
      sourceLabel: "Poppy Project documentation",
      sourceUrl: "https://docs.poppy-project.org/en/"
    },
    {
      id: "hrp-2-laas",
      name: "HRP-2 at LAAS-CNRS",
      company: "LAAS-CNRS / CNRS-AIST JRL research context",
      city: "Toulouse",
      status: "Research platform present in France",
      statusTone: "research",
      mainUse: "Humanoid motion planning, locomotion and manipulation research.",
      mobility: "Japanese-developed humanoid platform used in French research at LAAS-CNRS/JRL context.",
      keyHardware: "HRP-2 is a humanoid research platform originally developed in Japan. The France connection is the Toulouse research presence, not French manufacturing origin.",
      autonomy: "Research platform. Autonomy depends on laboratory experiments and control software.",
      whyItMatters:
        "HRP-2 gave French humanoid labs access to a serious full-body research platform for motion planning, contacts and whole-body control.",
      sourceLabel: "LAAS-CNRS robotics context",
      sourceUrl: "https://www.laas.fr/en/"
    },
    {
      id: "uma-northstar",
      name: "UMA / Northstar",
      company: "UMA",
      city: "Paris",
      status: "Watchlist",
      statusTone: "watchlist",
      mainUse: "Reported industrial and commercial robotics direction; public robot details remain limited.",
      mobility: "Not confirmed publicly enough for technical classification.",
      keyHardware: "No public hardware specification included here because official information is limited.",
      autonomy: "Not confirmed. This page does not assign autonomy claims to Northstar.",
      whyItMatters:
        "UMA belongs on the watchlist because the French humanoid startup signal is important, but the page keeps it separate from confirmed active robot platforms until official details are public.",
      sourceLabel: "Business Insider reporting",
      sourceUrl: "https://www.businessinsider.com/hugging-face-robotics-lead-startup-funding-uma-2025-7"
    }
  ],
  hubs: [
    {
      city: "Paris",
      flag: "🇫🇷",
      robots: ["Wandercraft Calvin", "Enchanted Tools Mirokaï", "Aldebaran legacy", "UMA watchlist"],
      detail: "Paris concentrates industrial humanoids, service robots, Aldebaran heritage and the next startup watchlist."
    },
    {
      city: "Bordeaux",
      flag: "🇫🇷",
      robots: ["Pollen Robotics Reachy 2", "Poppy ecosystem"],
      detail: "Bordeaux connects open-source humanoid hardware, embodied AI and education-oriented robotics."
    },
    {
      city: "Toulouse",
      flag: "🇫🇷",
      robots: ["LAAS-CNRS", "HRP-2 research platform"],
      detail: "Toulouse is important for humanoid research, motion planning, robotics labs and CNRS platforms."
    }
  ],
  timeline: [
    {
      label: "NAO",
      detail: "NAO helped define French humanoid robotics in education, programming labs and RoboCup-style research."
    },
    {
      label: "Romeo",
      detail: "Romeo explored assistive humanoid robotics with Aldebaran and French research partners."
    },
    {
      label: "Pepper",
      detail: "Pepper pushed social robotics into public-facing environments, then became a lesson in deployment economics."
    },
    {
      label: "Poppy",
      detail: "Poppy opened humanoid robotics to education, makers and open hardware experimentation."
    },
    {
      label: "Reachy 2",
      detail: "Reachy 2 connects French robotics to embodied AI, bimanual manipulation and open-source development."
    },
    {
      label: "Calvin",
      detail: "Calvin brings French humanoid robotics closer to industrial work in factories and logistics."
    }
  ],
  classifications: [
    {
      label: "Current programmes, including development",
      robots: ["Calvin, reported factory use; series delivery planned", "Reachy 2", "Mirokaï", "Poppy Humanoid"]
    },
    {
      label: "Historical / legacy",
      robots: ["NAO", "Pepper", "Romeo"]
    },
    {
      label: "Research platform present in France",
      robots: ["HRP-2 at LAAS-CNRS"]
    },
    {
      label: "Watchlist",
      robots: ["UMA / Northstar"]
    }
  ]
};
